// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ActionMenuItem } from '@/components/action-menu-item';
import { CatalogPageHeader } from '@/components/catalog-page-header';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  ArrowDropDownRounded,
  ClearRounded,
  Inventory2Outlined,
  ListRounded,
  SettingsOutlined,
  SettingsRounded,
  ShoppingCartOutlined,
  UploadFileOutlined,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort, invert } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ItemSortField, ItemsSearchParams } from '@/items/api/types';
import { CreateItemDialog } from '@/items/components/create-item-dialog';
import { ItemDataImportDialog } from '@/items/components/item-data-import-dialog';
import { ItemsTable } from '@/items/components/items-table';
import { ItemsTableSkeleton } from '@/items/components/items-table-skeleton';
import {
  ITEM_TYPE_LABELS,
  ITEM_TYPE_TO_CATEGORY,
  type ItemType,
  ItemTypeFilter,
} from '@/items/constants/item-types';
import {
  Badge,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { debounce, omit } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';

/**
 * Filter options for item type toggle.
 */
const ITEM_TYPE_FILTER_OPTIONS: FilterOption<ItemTypeFilter>[] = [
  { value: 'all', label: 'All', icon: <ListRounded fontSize="small" /> },
  {
    value: 'SERVICE',
    label: ITEM_TYPE_LABELS.SERVICE,
    icon: <ShoppingCartOutlined fontSize="small" />,
  },
  {
    value: 'DEVICE',
    label: ITEM_TYPE_LABELS.DEVICE,
    icon: <Inventory2Outlined fontSize="small" />,
  },
  {
    value: 'BULK',
    label: ITEM_TYPE_LABELS.BULK,
    icon: <SettingsOutlined fontSize="small" />,
  },
];

interface Props {
  itemType: ItemTypeFilter;
  searchParams: ItemsSearchParams;
  initialItemName?: string;
  initialItemType?: ItemType;
}

// Help text for archive filter tooltip
const INCLUDE_ARCHIVED_TOOLTIP_TITLE =
  'Show all item records, including archived items. Archived items do not appear by default in the item list.';

export function ItemsListing(props: Props) {
  const { itemType, searchParams, initialItemName, initialItemType } = props;
  const navigate = useNavigate();
  const searchContainer = useRef<HTMLInputElement>(null);
  // If the 'new' search parameter is present, automatically open the creation dialog.
  const createItemDialog = useDisclosure(props.searchParams.new !== undefined);
  const bulkUploadDialog = useDisclosure();
  const settingsMenu = useDisclosure();
  const addItemMenu = useDisclosure();
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const addItemButtonGroupRef = useRef<HTMLDivElement>(null);

  const sort: Sort<ItemSortField> = {
    property: searchParams.sort ?? 'name',
    direction: searchParams.direction ?? 'asc',
  };

  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      if (query.length === 0) {
        navigate({
          to: '/catalog/items',
          search: omit(searchParams, 'page', 'query'),
        });
        return;
      }
      navigate({
        to: '/catalog/items',
        search: { ...omit(searchParams, 'page'), query },
      });
    }, 300);
  }, [navigate, searchParams]);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      updateQuery.cancel();
    };
  }, [updateQuery]);

  const handleTypeChange = (newType: ItemTypeFilter) => {
    if (newType === 'all') {
      navigate({
        to: '/catalog/items',
        search: omit(searchParams, 'page', 'type'),
      });
    } else {
      navigate({
        to: '/catalog/items',
        search: {
          ...omit(searchParams, 'page'),
          type: ITEM_TYPE_TO_CATEGORY[newType],
        },
      });
    }
  };

  const handleSort = (property: ItemSortField) => {
    if (property === sort.property) {
      navigate({
        to: '/catalog/items',
        search: {
          ...searchParams,
          direction: invert(sort.direction),
        },
      });
    } else {
      navigate({
        to: '/catalog/items',
        search: {
          ...searchParams,
          sort: property,
          direction: 'asc',
        },
      });
    }
  };

  const handlePageChange = (page: number) => {
    navigate({
      to: '/catalog/items',
      search: { ...searchParams, page },
    });
  };

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  const handleClearSearch = () => {
    if (searchContainer.current) {
      searchContainer.current.value = '';
    }
    updateQuery('');
  };

  const handleChangeArchivedFilter = (isArchived: boolean) => {
    if (isArchived) {
      navigate({
        to: '/catalog/items',
        search: { ...searchParams, archived: true },
      });
    } else {
      navigate({
        to: '/catalog/items',
        search: omit(searchParams, 'archived'),
      });
    }
  };

  // Closes the create item dialog and removes 'new' parameter from URL.
  const handleCloseCreateDialog = () => {
    createItemDialog.close();
    navigate({
      to: '/catalog/items',
      search: omit(searchParams, 'new', 'newType'),
    });
  };

  const isArchivedChecked = searchParams.archived ?? false;
  const isBadgeInvisible = searchParams.archived === undefined;

  return (
    <PageContent>
      {/* Header */}
      <CatalogPageHeader
        action={
          <>
            <ButtonGroup
              size="small"
              variant="contained"
              ref={addItemButtonGroupRef}
            >
              <Button
                startIcon={<AddRounded fontSize="small" />}
                onClick={createItemDialog.open}
              >
                Add item
              </Button>
              <Button onClick={addItemMenu.open}>
                <ArrowDropDownRounded fontSize="small" />
              </Button>
            </ButtonGroup>
            <Menu
              open={addItemMenu.isOpen}
              onClose={addItemMenu.close}
              onClick={addItemMenu.close}
              anchorEl={addItemButtonGroupRef.current}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ disablePadding: true }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 },
                },
              }}
            >
              <ActionMenuItem
                icon={<UploadFileOutlined color="action" fontSize="small" />}
                label="Bulk upload"
                description="Upload multiple items from a CSV file"
                onClick={bulkUploadDialog.open}
              />
            </Menu>
          </>
        }
      />

      {/* Table controls */}
      <TableControlContainer>
        <Stack direction="row" marginRight="auto" spacing={1}>
          <FilterToggleGroup<ItemTypeFilter>
            value={itemType}
            options={ITEM_TYPE_FILTER_OPTIONS}
            onChange={handleTypeChange}
            ariaLabel="Filter by item type"
          />
        </Stack>
        <TextField
          inputRef={searchContainer}
          label="Search items"
          size="small"
          defaultValue={searchParams.query ?? ''}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleSearch}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
          InputProps={{
            endAdornment: searchParams.query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                  aria-label="Clear search"
                >
                  <ClearRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <IconButton ref={settingsButtonRef} onClick={settingsMenu.open}>
          <Badge variant="dot" color="error" invisible={isBadgeInvisible}>
            <SettingsRounded fontSize="small" />
          </Badge>
        </IconButton>
        <Popover
          anchorEl={settingsButtonRef.current}
          open={settingsMenu.isOpen}
          onClose={settingsMenu.close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Stack paddingY={0.5}>
            <Tooltip
              arrow
              placement="left"
              title={INCLUDE_ARCHIVED_TOOLTIP_TITLE}
            >
              <FormControlLabel
                sx={{ paddingLeft: 2, paddingY: 0.5 }}
                label={
                  <Typography variant="body2">
                    Include archived items
                  </Typography>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={isArchivedChecked}
                    onChange={(_, isChecked) =>
                      handleChangeArchivedFilter(isChecked)
                    }
                  />
                }
              />
            </Tooltip>
          </Stack>
        </Popover>
      </TableControlContainer>

      {/* Items table */}
      <SupportErrorBoundary message="An error occurred while loading items.">
        <Suspense fallback={<ItemsTableSkeleton />}>
          <ItemsTable
            itemType={itemType}
            searchQuery={searchParams.query}
            sort={sort}
            onSort={handleSort}
            page={searchParams.page}
            onPageChange={handlePageChange}
            includeArchived={searchParams.archived}
          />
        </Suspense>
      </SupportErrorBoundary>

      {/* Create item dialog */}
      <CreateItemDialog
        isOpen={createItemDialog.isOpen}
        onClose={handleCloseCreateDialog}
        initialName={initialItemName}
        initialType={initialItemType}
      />

      {/* Bulk upload dialog */}
      <ItemDataImportDialog
        isOpen={bulkUploadDialog.isOpen}
        onClose={bulkUploadDialog.close}
      />
    </PageContent>
  );
}
